import { BrowserRouter, Route, Navigate } from 'react-router-dom';
import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { ConfigProvider, App as AntApp, theme as antTheme } from 'antd';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { AuthProvider } from './contexts/AuthContext.tsx';
import { useAuth } from './hooks/useAuth.tsx';
import { ConfigProvider as ClientConfigProvider } from './contexts/ConfigContext';
import { SeasonProvider } from './contexts/SeasonContext';
import { EventProvider } from './contexts/EventContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ChartAnimationsProvider } from './contexts/ChartAnimationsContext';
import { TopBarStationProvider } from './contexts/TopBarStationContext';
import { SelectedTimestampProvider } from './contexts/SelectedTimestampContext';
import { StationObservationsProvider } from './contexts/StationObservationsContext';
import { AlertNotificationProvider } from './contexts/AlertNotificationContext';
import { ForecastFieldsProvider } from './contexts/ForecastFieldsContext';
import { useTheme } from './contexts/useTheme';
import { AlertToastManager } from './components/AlertNotifications';
import { LoginPage } from './components/Auth/LoginPage';
import { CheckToken } from './components/Auth/CheckToken';
import { AppLayout } from './components/Layout/AppLayout';
import { MobileLayout } from './components/Mobile/MobileLayout';
import { useIsMobile } from './hooks/useIsMobile';
import { ClimateRecapPage } from './pages/ClimateRecap';
import { ForecastPage } from './pages/Forecast';
import { NowcastingPage } from './pages/Nowcasting';
import { ObservationsPage } from './pages/Observations';
import { MessagesPage } from './pages/Messages';
import { AlertsPage } from './pages/Alerts';
import { ForecastFieldsManagement } from './pages/ForecastFieldsManagement/ForecastFieldsManagement';
import { ObservationFieldsManagement } from './pages/ObservationFieldsManagement/ObservationFieldsManagement';
import { CircuitsClimatePage } from './pages/CircuitsClimate';
import { CircuitsManagement } from './pages/CircuitsManagement';
import { TeamsManagement } from './pages/TeamsManagement';
import { SystemConfigManagement } from './pages/SystemConfigManagement';
import { JobsPage } from './pages/Jobs/index.ts';
import { ForecastManagementPage } from './pages/ForecastManagement';
import { EventsMap } from './pages/EventsMap';
import { FeedbackPage, AdminFeedbackPage, TeamFeedbackPage } from './pages/Feedback';
import { RainForecastsPage } from './pages/RainForecasts';
import { ApiDocsPage } from './pages/ApiDocs';
import { PortalDocsPage } from './pages/PortalDocs';
import { AdminDocsPage } from './pages/AdminDocs';
import { TimelinesApiPage } from './pages/TimelinesAPI';
import { RadarStatusPage } from './pages/RadarStatus/RadarStatusPage';
import { StationsStatusPage } from './pages/StationsStatus/StationsStatusPage';
import { StationIngestPage } from './pages/StationIngest';
import { RadarL2IngestPage } from './pages/RadarL2Ingest';
import { UserManagementPage } from './pages/UserManagement';
import { DashboardsPage, DashboardViewPage } from './pages/Dashboards';
import { ROUTES } from './utils/constants';
import { canAccessDashboards } from './utils/dashboardAccess';
import { getRecaptchaConfig } from './services/clientConfigService';
import { isTokenExpired } from './utils/helpers';
import './styles/theme.css';

// Protected Route wrapper component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { authState } = useAuth();
  
  // Check if user is authenticated and token is valid
  const isValidAuth = authState.isAuthenticated && 
                      authState.token && 
                      !isTokenExpired(authState.token);
  
  if (!isValidAuth) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  
  return <>{children}</>;
};

// Root or account-admin route - redirects others to climate-recap
const RootOrAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { authState } = useAuth();
  const isMobile = useIsMobile();

  const isValidAuth = authState.isAuthenticated &&
                      authState.token &&
                      !isTokenExpired(authState.token);

  if (!isValidAuth) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  const allowedRoles = ['root', 'account-admin'];
  if (!authState.user?.role || !allowedRoles.includes(authState.user.role)) {
    return <Navigate to={isMobile ? ROUTES.FORECAST : ROUTES.CLIMATE_RECAP} replace />;
  }

  return <>{children}</>;
};

// Root-only route - redirects non-root users to climate-recap
const RootOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { authState } = useAuth();
  const isMobile = useIsMobile();
  
  const isValidAuth = authState.isAuthenticated && 
                      authState.token && 
                      !isTokenExpired(authState.token);
  
  if (!isValidAuth) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  
  if (authState.user?.role !== 'root') {
    return <Navigate to={isMobile ? ROUTES.FORECAST : ROUTES.CLIMATE_RECAP} replace />;
  }
  
  return <>{children}</>;
};

// Dashboard dev route - root + allowlisted email only
const DashboardDevRoute = ({ children }: { children: React.ReactNode }) => {
  const { authState } = useAuth();
  const isMobile = useIsMobile();

  const isValidAuth = authState.isAuthenticated &&
                      authState.token &&
                      !isTokenExpired(authState.token);

  if (!isValidAuth) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!canAccessDashboards(authState.user)) {
    return <Navigate to={isMobile ? ROUTES.FORECAST : ROUTES.CLIMATE_RECAP} replace />;
  }

  return <>{children}</>;
};

// Public Route wrapper (redirects to appropriate default if already authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { authState } = useAuth();
  const isMobile = useIsMobile();
  
  const isValidAuth = authState.isAuthenticated && 
                      authState.token && 
                      !isTokenExpired(authState.token);
  
  if (isValidAuth) {
    return <Navigate to={isMobile ? ROUTES.FORECAST : ROUTES.CLIMATE_RECAP} replace />;
  }
  
  return <>{children}</>;
};

// Get reCAPTCHA config at module level (needed before login)
const recaptchaConfig = getRecaptchaConfig();

/** Redirects to forecast on mobile, climate-recap on desktop */
function IndexRedirect() {
  const isMobile = useIsMobile();
  return <Navigate to={isMobile ? ROUTES.FORECAST : ROUTES.CLIMATE_RECAP} replace />;
}

/** Renders MobileLayout on mobile, AppLayout on desktop */
function ConditionalLayout({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <div
        style={{
          height: '100vh',
          maxHeight: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <MobileLayout />
      </div>
    );
  }
  return <>{children}</>;
}

function AppContent() {
  const { theme: currentTheme } = useTheme();

  const routedApp = (
      <BrowserRouter>
        <CheckToken />
        <AlertToastManager />
        <Routes>
          {/* Public Routes */}
          <Route
            path={ROUTES.LOGIN}
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <ConditionalLayout>
                  <AppLayout />
                </ConditionalLayout>
              </ProtectedRoute>
            }
          >
            <Route index element={<IndexRedirect />} />
            <Route path={ROUTES.CLIMATE_RECAP} element={<ClimateRecapPage />} />
            <Route path={ROUTES.FORECAST} element={<ForecastPage />} />
            <Route path={ROUTES.NOWCASTING} element={<NowcastingPage />} />
            <Route path={ROUTES.OBSERVATIONS} element={<ObservationsPage />} />
            <Route path={ROUTES.MESSAGES} element={<MessagesPage />} />
            <Route path={ROUTES.ALERTS} element={<AlertsPage />} />
            <Route path={ROUTES.RAIN_FORECASTS} element={<RainForecastsPage />} />
            <Route path={ROUTES.FORECAST_FIELDS} element={<ForecastFieldsManagement />} />
            <Route path={ROUTES.OBSERVATION_FIELDS} element={<ObservationFieldsManagement />} />
            <Route path={ROUTES.CIRCUITS_CLIMATE} element={<CircuitsClimatePage />} />
            <Route path={ROUTES.CIRCUITS} element={<CircuitsManagement />} />
            <Route path={ROUTES.TEAMS} element={<TeamsManagement />} />
            <Route path={ROUTES.SYSTEM_CONFIG} element={<SystemConfigManagement />} />
            <Route path={ROUTES.JOBS} element={<JobsPage />} />
            <Route
              path={ROUTES.FORECAST_MANAGEMENT}
              element={
                <RootOnlyRoute>
                  <ForecastManagementPage />
                </RootOnlyRoute>
              }
            />
            <Route path={ROUTES.EVENTS} element={<EventsMap />} />
            <Route path={ROUTES.FEEDBACK} element={<FeedbackPage />} />
            <Route path={ROUTES.FEEDBACK_TEAM} element={<TeamFeedbackPage />} />
            <Route path={ROUTES.FEEDBACK_MANAGEMENT} element={<AdminFeedbackPage />} />
            <Route path={ROUTES.API_DOCS} element={<ApiDocsPage />} />
            <Route path={`${ROUTES.API_DOCS}/:section/:page`} element={<ApiDocsPage />} />
            <Route path={`${ROUTES.API_DOCS}/:page`} element={<ApiDocsPage />} />
            <Route path={ROUTES.PORTAL_DOCS} element={<PortalDocsPage />} />
            <Route path={`${ROUTES.PORTAL_DOCS}/:section/:page`} element={<PortalDocsPage />} />
            <Route path={`${ROUTES.PORTAL_DOCS}/:page`} element={<PortalDocsPage />} />
            <Route
              path={ROUTES.ADMIN_DOCS}
              element={
                <RootOnlyRoute>
                  <AdminDocsPage />
                </RootOnlyRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN_DOCS}/:section/:page`}
              element={
                <RootOnlyRoute>
                  <AdminDocsPage />
                </RootOnlyRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN_DOCS}/:page`}
              element={
                <RootOnlyRoute>
                  <AdminDocsPage />
                </RootOnlyRoute>
              }
            />
            <Route path={ROUTES.TIMELINES_API} element={<TimelinesApiPage />} />
            <Route
              path={ROUTES.RADAR_STATUS}
              element={
                <RootOnlyRoute>
                  <RadarStatusPage />
                </RootOnlyRoute>
              }
            />
            <Route
              path={ROUTES.STATIONS_STATUS}
              element={
                <RootOnlyRoute>
                  <StationsStatusPage />
                </RootOnlyRoute>
              }
            />
            <Route
              path={ROUTES.STATION_INGEST}
              element={
                <RootOnlyRoute>
                  <StationIngestPage />
                </RootOnlyRoute>
              }
            />
            <Route
              path={ROUTES.RADAR_L2_INGEST}
              element={
                <RootOnlyRoute>
                  <RadarL2IngestPage />
                </RootOnlyRoute>
              }
            />
            <Route
              path={ROUTES.USER_MANAGEMENT}
              element={
                <RootOrAdminRoute>
                  <UserManagementPage />
                </RootOrAdminRoute>
              }
            />
            <Route
              path={ROUTES.DASHBOARDS}
              element={
                <DashboardDevRoute>
                  <DashboardsPage />
                </DashboardDevRoute>
              }
            />
            <Route
              path={`${ROUTES.DASHBOARDS}/:id`}
              element={
                <DashboardDevRoute>
                  <DashboardViewPage />
                </DashboardDevRoute>
              }
            />
          </Route>

          {/* Catch all - redirect to login */}
          <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
        </Routes>
      </BrowserRouter>
  );

  const themedApp = (
    <ConfigProvider
      theme={{
        algorithm: currentTheme === 'dark' ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 6,
        },
      }}
    >
      {recaptchaConfig.enabled && recaptchaConfig.siteKey ? (
        <GoogleReCaptchaProvider reCaptchaKey={recaptchaConfig.siteKey}>
          {routedApp}
        </GoogleReCaptchaProvider>
      ) : (
        routedApp
      )}
    </ConfigProvider>
  );

  return themedApp;
}

function App() {
  return (
    <AntApp>
      <AuthProvider>
        <ClientConfigProvider>
        <ThemeProvider>
          <ChartAnimationsProvider>
          <TopBarStationProvider>
          <SeasonProvider>
            <EventProvider>
              <AlertNotificationProvider>
                <SelectedTimestampProvider>
                  <ForecastFieldsProvider>
                    <StationObservationsProvider>
                      <AppContent />
                    </StationObservationsProvider>
                  </ForecastFieldsProvider>
                </SelectedTimestampProvider>
              </AlertNotificationProvider>
            </EventProvider>
          </SeasonProvider>
          </TopBarStationProvider>
          </ChartAnimationsProvider>
        </ThemeProvider>
        </ClientConfigProvider>
      </AuthProvider>
    </AntApp>
  );
}

export default App;
